<template>
    <div
        class="root with-overlay"
        :class="{ 'has-overlay': hasOverlay }"
    >
        <TheMenu
            v-show="!hideMenu"
        />
        <div
            v-if="!isLoaded"
            id="global-loader-wrapper"
            class="global-loader-wrapper"
        >
            <div class="global-loader">
                <LottieAnimation
                    loop
                    :animation-data="loader"
                />
            </div>
        </div>
        <TheStoreSidebar />
        <RouterView
            v-if="isRouteAllowed"
            class="page-container"
            @success-login="onSuccessLogin"
        />
        <TheNotification />
    </div>
</template>

<script lang="ts" setup>
import {
    ref, computed, onMounted, onBeforeUnmount, markRaw,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { LottieAnimation } from 'lottie-web-vue';
import TheNotification from '../../components/TheNotification.vue';
import Loader from '../../../animations/loader.json';
import TheMenu from '../../components/Menu/TheMenu.vue';
import TheStoreSidebar from '../../components/Sidebar/TheStoreSidebar.vue';

import { useUserStore } from '@/vue/stores/user';
import { useNotificationStore } from '@/vue/stores/notification';
import { usePermissionsStore } from '../../stores/permissions';
import { useLoadingStore } from '../../stores/loading';
import { useMenuStore } from '../../stores/menu';
import { useColorsStore } from '../../stores/colors';
import { useSyncStore } from '../../stores/sync';

import ROUTE_NAMES from '../../routes/routeNames';
import { getUserSessionState } from '@/vue/features/shared/auth/api/userSessionApi';
import { leaveUserSession } from '@/vue/features/shared/auth/services/userSession';
import { addOnWowzaLowSpaceHandler } from '../../../scripts/api/wowza/SpaceCheckApi';
import { Notification } from '../../../scripts/helpers/Notification';
import { NotificationVariant } from '../../../scripts/helpers/Notification/Notification';
import { Variant } from '../../components/Elements/Button/Variant';
import { addOnUserLogoutSoonHandler, getPasswordExpirationInfo } from '../../../scripts/api/user/userApi';
import HistoryEventBus, { HISTORY_POP_EVENT } from '../../Event/HistoryEventBus';
import type { LoginResponse } from '../../../scripts/api/login/LoginApi';
import LockIcon from '../../../images/lock.svg?component';
import PasswordExpirationIcon from '../../../images/password-expiration.svg?component';
import { loadInitialData } from '../../app/initialLoader';
import { useUserTheme } from '../../composables/theme/userTheme';
import { useUserDetailsSync } from '../../composables/userDetailsSync';
import { socket } from '../../../scripts/socket';
import type { ScheduleMessage } from '../../../scripts/types/sockets/schedule';
import { ScheduleMessageC, Type } from '../../../scripts/types/sockets/schedule';
import { decodeAsTypeC } from '../../../scripts/typeAssertions/isTypeC';
import NotificationButton from '../../../scripts/helpers/Notification/NotificationButton';
import { useIsAnonymousStore } from '@/vue/stores/isAnonymous';

const userStore = useUserStore();

const loader = Loader;

const { isLoaded, hasAjaxRequests } = storeToRefs(useLoadingStore());
const { doesRequestOverlay } = storeToRefs(useMenuStore());
const colorsStore = useColorsStore();
const syncStore = useSyncStore();
const isAnonymousStore = useIsAnonymousStore();
const notificationStore = useNotificationStore();

const CHECK_SESSION_INTERVAL_IN_MILLISECONDS = 60000;

const checkUserSessionTimer = ref<ReturnType<typeof setTimeout> | null>(null);

const hasOverlay = computed(
    () => !isLoaded.value
        || notificationStore.notification.show
        || doesRequestOverlay.value
        || hasAjaxRequests.value,
);

const route = useRoute();

const router = useRouter();

const isRouteAllowed = computed(
    () => (userStore.arePermissionsSet && usePermissionsStore().areAllPermissionsSet)
        || isAnonymousStore.isAnonymous,
);

const subscribeToLowServerSpace = () => {
    if (userStore.hasPermission(usePermissionsStore().allPermissions.general)) {
        addOnWowzaLowSpaceHandler(({ text, title }) => {
            const notification = new Notification({
                title,
                text,
                icon: null,
                variant: NotificationVariant.TITLE_WITH_MESSAGE,
                withCancel: true,
                cancelText: 'Got it',
                cancelVariant: Variant.DARK,
            });
            notificationStore.promptUser(notification);
        });
    }
};

const subscribeToUserLogoutSoon = () => {
    addOnUserLogoutSoonHandler(({ text, title }) => {
        const notification = new Notification({
            title,
            text,
            icon: markRaw(LockIcon),
            variant: NotificationVariant.TITLE_WITH_MESSAGE,
            withCancel: true,
            cancelText: 'Got it',
            cancelVariant: Variant.DARK,
        });
        notificationStore.promptUser(notification);
    });
};

const checkUserSession = async () => {
    const isLoginPage = route.name === ROUTE_NAMES.LOGIN_MAIN;
    const isAnonymousPage = isAnonymousStore.isAnonymous;
    if (isLoginPage || isAnonymousPage) {
        return;
    }

    const sessionState = await getUserSessionState();
    if (sessionState.shouldLogout) {
        await leaveUserSession(sessionState);
    }
};

const enableUserSessionValidation = () => {
    if (checkUserSessionTimer.value) {
        return;
    }
    checkUserSessionTimer.value = setInterval(
        checkUserSession, CHECK_SESSION_INTERVAL_IN_MILLISECONDS,
    );
};

const addBackForwardButtonsRootEvent = () => {
    window.onpopstate = () => {
        HistoryEventBus.emit(HISTORY_POP_EVENT, HISTORY_POP_EVENT);
    };
};

const showPasswordExpirationWarningIfNeeded = async (): Promise<void> => {
    if (isAnonymousStore.isAnonymous) {
        return;
    }
    if (userStore.isPasswordExpired) {
        return;
    }
    try {
        const { shouldShowAlert, days } = await getPasswordExpirationInfo();
        if (!shouldShowAlert || days === null) {
            return;
        }
        const notification = new Notification({
            title: `Password expiration in ${days} day${days === 1 ? '' : 's'}`,
            text: `Your password will expire in ${days} day${days === 1 ? '' : 's'}.\nPlease, change it.`,
            icon: markRaw(PasswordExpirationIcon),
            variant: NotificationVariant.TITLE_WITH_MESSAGE,
            withCancel: true,
            cancelText: 'Later',
            cancelVariant: Variant.GRAY,
        });
        notification.addActionButton(new NotificationButton('Change password', () => {
            router.push({ name: ROUTE_NAMES.USER_SETTINGS });
        }));
        notificationStore.promptUser(notification);
    } catch (e) {
        console.warn('Password expiration check failed', e);
    }
};

onMounted(() => {
    void colorsStore.hydrate();
    subscribeToLowServerSpace();
    subscribeToUserLogoutSoon();
    if (!isAnonymousStore.isAnonymous) {
        enableUserSessionValidation();
    }
    addBackForwardButtonsRootEvent();
});

const disableUserSessionValidation = () => {
    if (checkUserSessionTimer.value) {
        clearInterval(checkUserSessionTimer.value);
        checkUserSessionTimer.value = null;
    }
};

onBeforeUnmount(() => {
    disableUserSessionValidation();
    syncStore.unconfigureSync();
});

const onSuccessLogin = async (response: LoginResponse) => {
    const redirectPath = response.redirect ? new URL(response.redirect).pathname : null;
    await loadInitialData();

    if (!isAnonymousStore.isAnonymous) {
        enableUserSessionValidation();
    }
    if (userStore.isPasswordExpired) {
        await router.push({ name: ROUTE_NAMES.USER_SETTINGS });
        return;
    }

    void showPasswordExpirationWarningIfNeeded();

    if (redirectPath) {
        await router.push(redirectPath);
    } else {
        await router.push({ name: ROUTE_NAMES.HOME });
    }
};

const hideMenu = computed(() => Boolean(route.name === undefined || route.meta?.hideMenu));

useUserTheme();
useUserDetailsSync();

const scheduleListCallback = async () => {
    await router.push({
        name: ROUTE_NAMES.SCHEDULE_LIST,
    });
};

const onMessage = async (message: ScheduleMessage): Promise<void> => {
    if (message.type === Type.ERROR && message.createdBy === userStore.userId) {
        const notification = new Notification({
            title: `The schedule ${message.name} failed to start`,
            text: '',
            variant: NotificationVariant.TITLE_WITH_MESSAGE,
            withCancel: true,
            cancelText: 'Cancel',
            cancelVariant: Variant.GRAY,
            iconVariant: Variant.RED,
        });

        notification.addActionButton(new NotificationButton('See schedules list', scheduleListCallback));

        notificationStore.promptUser(notification);
    }
};

socket.on('schedule', (json: string) => {
    const parsedJson = JSON.parse(json);
    const message = decodeAsTypeC(parsedJson, ScheduleMessageC);
    onMessage(message);
});

</script>

<style lang="scss">
@import '../../../styles/abstracts/z-indexes';

/* stylelint-disable selector-max-id */
#app {
    height: 100%;
}

.root {
    display: flex;

    flex-grow: 1;
    flex-direction: row;

    align-items: stretch;
    justify-content: stretch;

    width: 100%;
    height: 100%;

    overflow-y: hidden;
}

.page-container {
    display: flex;

    flex-grow: 1;

    min-width: 0;
    min-height: 100%;

    background-color: var(--theme-color-surface-background);
}

.global-loader-wrapper {
    position: fixed;
    top: 0;

    z-index: $loader-z-index;

    display: flex;

    align-items: center;

    justify-content: center;

    width: 100%;
    height: 100%;

    background: #000;
    opacity: 0.6;
}

.global-loader {
    width: 7rem;
}
</style>
